/* eslint-disable no-underscore-dangle */
import { createApp, createSSRApp } from 'vue'

// default plugins
import Router from './router'
import Http from './http'
import Logger from './logger'
import CrawlerSpamPlugin from './crawler-spam'
import ErrorTracker from './errorTracker'

import { installPlugin, clearCachedPlugins } from './helper/pluginRegister'
import DefaultAppContainer from './DefaultAppContainer.vue'
import pkg from '../package.json'

function isBrowser() {
  return process.browser === true
}

function argsCheck(args) {
  const template = args[0]
  const config = args[1]

  if (typeof template === 'undefined') {
    throw new Error('[Launcher Exception]: {template} is required!')
  }
  if (typeof config.routes === 'undefined') {
    throw new Error('[Launcher Exception]: {config.routes} is required!')
  }
}

class Launcher {
  static version = pkg.version

  static http = Http

  static logger = Logger

  static isBrowser = isBrowser()

  constructor(template, config = {}) {
    argsCheck(arguments) // eslint-disable-line

    this.ops = {}
    this.ops.template = template
    this.ops.config = config
    this.ops.config.platform = this.ops.config.platform || 'pc'
    this.plugins = []
    const AppContainer = config.appContainer || DefaultAppContainer

    // Work around for no store env renderer
    this.store = {
      state: {},
    }

    if (isBrowser()) {
      this._createVm(AppContainer)
      this._installBuiltInPlugins()
    }
  }

  start(...args) {
    if (args.length) {
      throw new Error("[Launcher Exception]: AppContainer has been moved to constructor, use `new Launcher('#app', { appContainer: Container })` instead.")
    }
    if (isBrowser()) {
      // 微前端场景，清除状态
      clearCachedPlugins()

      this._installPlugins()

      const { router, app } = this
      /**
       * https://v3.vuejs.org/guide/ssr/routing.html#code-splitting
       * 挂载 app 之前调用 router.onReady，因为路由器必须要提前解析路由配置中的异步组件，才能正确地调用组件中可能存在的路由钩子
       */
      router.isReady().then(() => app.mount(this.ops.template))
    }

    Logger.debug(`[Launcher] Starting in \`${process.env.NODE_ENV}\` env, Vue v${this.app.version}`)
  }

  _createVm(AppContainer) {
    this.vm = (process.env.BROWSER && !window.__INITIAL_SSR_STATE__) ? createApp(AppContainer) : createSSRApp(AppContainer)
    this.app = this.vm
    this._configGlobalProperties()
  }

  _configGlobalProperties() {
    this.vm.config.globalProperties.isBrowser = isBrowser()
  }

  _installBuiltInPlugins() {
    const {
      routes,
      router,
      logger,
      http,
    } = this.ops.config

    installPlugin(this, Launcher, { routes, routerOptions: router }, Router).create()
    installPlugin(this, Launcher, logger, Logger).create()
    installPlugin(this, Launcher, {}, CrawlerSpamPlugin).create()
    if (http) {
      installPlugin(this, Launcher, http, Http).create()
    }
  }

  use(plugin, ops) {
    this.plugins.push({
      plugin,
      ops,
      priority: plugin.name === 'store' ? 0 : 99,
    })
  }

  _installPlugins() {
    const plugins = this.plugins

    // 调整优先级
    plugins.sort((a, b) => a.priority - b.priority)

    plugins.forEach(item => {
      const { plugin, ops } = item
      installPlugin(this, Launcher, ops, plugin).create()
    })
  }
}

class LauncherFactory {
  static http = Http

  static logger = Logger

  static isBrowser = isBrowser()

  constructor(template, config = {}) {
    if (isBrowser()) {
      const launcher = new Launcher(template, config)
      Object.setPrototypeOf(this, launcher)
    } else {
      this.plugins = []
      this.vmPlugins = []
      this.template = template
      this.config = config

      this.vm = {
        use: plugin => this.vmPlugins.push(plugin),
      }
    }
  }

  create({ context } = {}) {
    const launcher = new Launcher(this.template, this.config)

    launcher.context = context

    launcher._createVm(this.config.appContainer || DefaultAppContainer)
    if (this.vmPlugins?.length) {
      this.vmPlugins.forEach(e => {
        launcher.vm.use(e)
      })
    }

    clearCachedPlugins()
    launcher._installBuiltInPlugins()

    launcher.plugins = this.plugins
    launcher._installPlugins()

    const render = () => import('./render')
    launcher.render = render

    return launcher
  }

  // eslint-disable-next-line
  start(...args) {
    if (args.length) {
      throw new Error("[Launcher Exception]: AppContainer has been moved to constructor, use `new Launcher('#app', { appContainer: Container })` instead.")
    }
    return '--ssr--'
  }

  use(plugin, ops) {
    this.plugins.push({
      plugin,
      ops,
      priority: plugin.name === 'store' ? 0 : 99,
    })
  }
}

// Export
export default LauncherFactory
export const errorTracker = ErrorTracker
export const http = Http
export const logger = Logger
